import React, { useState, useEffect, useCallback } from 'react'
import {
  Typography,
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tabs,
  message,
  Tooltip,
  Upload,
  Image,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Empty,
} from 'antd'
import {
  FileTextOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  UploadOutlined,
  CopyOutlined,
  CheckOutlined,
  CloseOutlined,
  PictureOutlined,
  FolderOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons'
import { blogAPI } from '../services/api'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

const Blog = () => {
  // State
  const [posts, setPosts] = useState([])
  const [categories, setCategories] = useState([])
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [postModalVisible, setPostModalVisible] = useState(false)
  const [categoryModalVisible, setCategoryModalVisible] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [editingCategory, setEditingCategory] = useState(null)
  const [activeTab, setActiveTab] = useState('posts')
  const [statusFilter, setStatusFilter] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [featuredImageLoading, setFeaturedImageLoading] = useState(false)

  const [postForm] = Form.useForm()
  const [categoryForm] = Form.useForm()

  const isMobile = window.innerWidth < 768

  // Fetch data
  const fetchPosts = useCallback(async () => {
    setLoading(true)
    try {
      const params = { include_archived: true }
      if (statusFilter) params.status_filter = statusFilter
      if (categoryFilter) params.category = categoryFilter
      const data = await blogAPI.listPosts(params)
      setPosts(data)
    } catch (error) {
      console.error('Failed to fetch posts:', error)
      message.error('Failed to load blog posts')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, categoryFilter])

  const fetchCategories = useCallback(async () => {
    try {
      const data = await blogAPI.listCategories()
      setCategories(data)
    } catch (error) {
      console.error('Failed to fetch categories:', error)
      message.error('Failed to load categories')
    }
  }, [])

  const fetchImages = useCallback(async () => {
    try {
      const data = await blogAPI.listImages()
      setImages(data)
    } catch (error) {
      console.error('Failed to fetch images:', error)
      message.error('Failed to load images')
    }
  }, [])

  useEffect(() => {
    fetchPosts()
    fetchCategories()
    fetchImages()
  }, [fetchPosts, fetchCategories, fetchImages])

  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
  }

  // Post handlers
  const handleCreatePost = () => {
    setEditingPost(null)
    postForm.resetFields()
    postForm.setFieldsValue({
      author: 'ACIS Team',
      status: 'draft',
      featured: false,
    })
    setPostModalVisible(true)
  }

  const handleEditPost = (record) => {
    setEditingPost(record)
    postForm.setFieldsValue({
      title: record.title,
      slug: record.slug,
      excerpt: record.excerpt,
      content: record.content,
      author: record.author,
      category: record.category,
      tags: record.tags?.join(', ') || '',
      featured: record.featured,
      image_url: record.image_url,
      read_time: record.read_time,
      status: record.status,
      meta_title: record.meta_title,
      meta_description: record.meta_description,
    })
    setPostModalVisible(true)
  }

  const handlePostSubmit = async (values) => {
    try {
      const data = {
        ...values,
        tags: values.tags ? values.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      }

      if (editingPost) {
        await blogAPI.updatePost(editingPost.id, data)
        message.success('Post updated successfully')
      } else {
        await blogAPI.createPost(data)
        message.success('Post created successfully')
      }

      setPostModalVisible(false)
      postForm.resetFields()
      setEditingPost(null)
      fetchPosts()
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to save post')
    }
  }

  const handleDeletePost = async (record) => {
    try {
      await blogAPI.deletePost(record.id)
      message.success('Post archived')
      fetchPosts()
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete post')
    }
  }

  const handlePublishToggle = async (record) => {
    try {
      if (record.status === 'published') {
        await blogAPI.unpublishPost(record.id)
        message.success('Post unpublished')
      } else {
        await blogAPI.publishPost(record.id)
        message.success('Post published')
      }
      fetchPosts()
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to update post status')
    }
  }

  // Category handlers
  const handleCreateCategory = () => {
    setEditingCategory(null)
    categoryForm.resetFields()
    categoryForm.setFieldsValue({ sort_order: 0 })
    setCategoryModalVisible(true)
  }

  const handleEditCategory = (record) => {
    setEditingCategory(record)
    categoryForm.setFieldsValue({
      name: record.name,
      slug: record.slug,
      description: record.description,
      sort_order: record.sort_order,
    })
    setCategoryModalVisible(true)
  }

  const handleCategorySubmit = async (values) => {
    try {
      if (editingCategory) {
        await blogAPI.updateCategory(editingCategory.id, values)
        message.success('Category updated')
      } else {
        await blogAPI.createCategory(values)
        message.success('Category created')
      }

      setCategoryModalVisible(false)
      categoryForm.resetFields()
      setEditingCategory(null)
      fetchCategories()
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to save category')
    }
  }

  const handleDeleteCategory = async (record) => {
    try {
      await blogAPI.deleteCategory(record.id)
      message.success('Category deleted')
      fetchCategories()
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete category')
    }
  }

  // Image handlers
  const handleImageUpload = async (file) => {
    setUploadLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const result = await blogAPI.uploadImage(formData)
      message.success(`Image uploaded: ${result.filename}`)
      fetchImages()
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to upload image')
    } finally {
      setUploadLoading(false)
    }
    return false // Prevent default upload
  }

  const handleDeleteImage = async (filename) => {
    try {
      await blogAPI.deleteImage(filename)
      message.success('Image deleted')
      fetchImages()
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete image')
    }
  }

  const copyImageUrl = (url) => {
    navigator.clipboard.writeText(url)
    message.success('URL copied to clipboard')
  }

  // Handle featured image upload directly in post form
  const handleFeaturedImageUpload = async (file) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      message.error('Please upload an image file')
      return false
    }

    if (file.size > 5 * 1024 * 1024) {
      message.error('Image must be smaller than 5MB')
      return false
    }

    setFeaturedImageLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await blogAPI.uploadImage(formData)
      // Set the image URL in the form
      postForm.setFieldsValue({ image_url: response.url })
      message.success('Image uploaded successfully')
      fetchImages() // Refresh images list
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to upload image')
    } finally {
      setFeaturedImageLoading(false)
    }
    return false // Prevent default upload behavior
  }

  // Table columns
  const postColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            {record.featured && <StarFilled style={{ color: '#faad14' }} />}
            <Text strong>{text}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            /{record.slug}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => <Tag color="blue">{cat}</Tag>,
      responsive: ['md'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          draft: 'default',
          published: 'success',
          archived: 'warning',
        }
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
      responsive: ['lg'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title={record.status === 'published' ? 'Unpublish' : 'Publish'}>
            <Button
              size="small"
              type={record.status === 'published' ? 'default' : 'primary'}
              icon={record.status === 'published' ? <CloseOutlined /> : <CheckOutlined />}
              onClick={() => handlePublishToggle(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditPost(record)}
            />
          </Tooltip>
          <Tooltip title="Preview">
            <Button
              size="small"
              icon={<EyeOutlined />}
              href={`https://acis-trading.com/blog/${record.slug}`}
              target="_blank"
            />
          </Tooltip>
          {record.status !== 'archived' && (
            <Popconfirm
              title="Archive this post?"
              onConfirm={() => handleDeletePost(record)}
              okText="Archive"
              okType="danger"
            >
              <Tooltip title="Archive">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const categoryColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            /{record.slug}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      responsive: ['md'],
    },
    {
      title: 'Order',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditCategory(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this category?"
            description="This will fail if posts are using it."
            onConfirm={() => handleDeleteCategory(record)}
            okText="Delete"
            okType="danger"
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // Stats
  const publishedCount = posts.filter((p) => p.status === 'published').length
  const draftCount = posts.filter((p) => p.status === 'draft').length
  const featuredCount = posts.filter((p) => p.featured).length

  // Tab items
  const tabItems = [
    {
      key: 'posts',
      label: (
        <span>
          <FileTextOutlined /> Posts ({posts.length})
        </span>
      ),
      children: (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 120 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Select.Option value="draft">Draft</Select.Option>
              <Select.Option value="published">Published</Select.Option>
              <Select.Option value="archived">Archived</Select.Option>
            </Select>
            <Select
              placeholder="Category"
              allowClear
              style={{ width: 150 }}
              value={categoryFilter}
              onChange={setCategoryFilter}
            >
              {categories.map((cat) => (
                <Select.Option key={cat.slug} value={cat.name}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Posts Table */}
          <Table
            columns={postColumns}
            dataSource={posts}
            rowKey="id"
            loading={loading}
            scroll={{ x: isMobile ? 500 : undefined }}
            pagination={{ pageSize: 10, showSizeChanger: !isMobile }}
          />
        </>
      ),
    },
    {
      key: 'categories',
      label: (
        <span>
          <FolderOutlined /> Categories ({categories.length})
        </span>
      ),
      children: (
        <>
          <div className="mb-4">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateCategory}>
              Add Category
            </Button>
          </div>
          <Table
            columns={categoryColumns}
            dataSource={categories}
            rowKey="id"
            pagination={false}
          />
        </>
      ),
    },
    {
      key: 'images',
      label: (
        <span>
          <PictureOutlined /> Images ({images.length})
        </span>
      ),
      children: (
        <>
          {/* Upload Button */}
          <div className="mb-4">
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={uploadLoading}
              >
                Upload Image
              </Button>
            </Upload>
            <Text type="secondary" className="ml-2">
              Max 5MB. JPEG, PNG, WebP, GIF
            </Text>
          </div>

          {/* Image Grid */}
          {images.length === 0 ? (
            <Empty description="No images uploaded yet" />
          ) : (
            <Row gutter={[16, 16]}>
              {images.map((img) => (
                <Col xs={12} sm={8} md={6} lg={4} key={img.filename}>
                  <Card
                    size="small"
                    cover={
                      <Image
                        src={img.url}
                        alt={img.filename}
                        style={{ height: 100, objectFit: 'cover' }}
                        preview={{ mask: 'Preview' }}
                      />
                    }
                    actions={[
                      <Tooltip title="Copy URL" key="copy">
                        <CopyOutlined onClick={() => copyImageUrl(img.url)} />
                      </Tooltip>,
                      <Popconfirm
                        title="Delete this image?"
                        onConfirm={() => handleDeleteImage(img.filename)}
                        key="delete"
                      >
                        <DeleteOutlined style={{ color: '#ff4d4f' }} />
                      </Popconfirm>,
                    ]}
                  >
                    <Card.Meta
                      description={
                        <Text ellipsis style={{ fontSize: 11 }}>
                          {img.filename}
                        </Text>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      ),
    },
  ]

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <Title level={2} className="mb-0">
          <FileTextOutlined className="mr-2" />
          Blog Management
        </Title>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreatePost}
          >
            New Post
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchPosts()
              fetchCategories()
              fetchImages()
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Stats */}
      <Row gutter={16} className="mb-6">
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Posts"
              value={posts.length}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Published"
              value={publishedCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Drafts"
              value={draftCount}
              valueStyle={{ color: '#faad14' }}
              prefix={<EditOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Featured"
              value={featuredCount}
              valueStyle={{ color: '#1890ff' }}
              prefix={<StarFilled />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Post Modal */}
      <Modal
        title={editingPost ? 'Edit Blog Post' : 'Create Blog Post'}
        open={postModalVisible}
        onCancel={() => {
          setPostModalVisible(false)
          postForm.resetFields()
          setEditingPost(null)
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={postForm}
          layout="vertical"
          onFinish={handlePostSubmit}
        >
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input
                  placeholder="Blog post title"
                  onChange={(e) => {
                    if (!editingPost) {
                      postForm.setFieldValue('slug', generateSlug(e.target.value))
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="slug"
                label="URL Slug"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="url-slug" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select placeholder="Select category">
                  {categories.map((cat) => (
                    <Select.Option key={cat.id} value={cat.name}>
                      {cat.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="author" label="Author">
                <Input placeholder="Author name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="excerpt"
            label="Excerpt"
            help="Short summary for cards (max 200 chars)"
          >
            <TextArea rows={2} maxLength={200} showCount />
          </Form.Item>

          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true, message: 'Required' }]}
            help="Supports Markdown formatting"
          >
            <TextArea rows={10} placeholder="Write your blog post content..." />
          </Form.Item>

          <Form.Item label="Featured Image">
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="image_url" noStyle>
                <Input
                  placeholder="/blog-images/example.jpg"
                  style={{ width: 'calc(100% - 110px)' }}
                />
              </Form.Item>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleFeaturedImageUpload}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={featuredImageLoading}
                >
                  Upload
                </Button>
              </Upload>
            </Space.Compact>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.image_url !== curr.image_url}>
              {({ getFieldValue }) => {
                const imageUrl = getFieldValue('image_url')
                return imageUrl ? (
                  <div style={{ marginTop: 8 }}>
                    <Image
                      src={imageUrl}
                      alt="Featured"
                      style={{ maxHeight: 100, objectFit: 'cover' }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAK2aGAQrSN9UAAAAASUVORK5CYII="
                    />
                  </div>
                ) : null
              }}
            </Form.Item>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="read_time" label="Read Time">
                <Input placeholder="5 min read" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Select.Option value="draft">Draft</Select.Option>
                  <Select.Option value="published">Published</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="featured" label="Featured" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="tags" label="Tags" help="Comma-separated">
            <Input placeholder="ai, trading, portfolios" />
          </Form.Item>

          <div className="border-t pt-4 mt-4">
            <Text type="secondary" className="block mb-2">
              SEO Settings
            </Text>
            <Form.Item name="meta_title" label="Meta Title">
              <Input placeholder="SEO title (defaults to post title)" maxLength={60} />
            </Form.Item>
            <Form.Item name="meta_description" label="Meta Description">
              <TextArea
                rows={2}
                placeholder="SEO description"
                maxLength={160}
                showCount
              />
            </Form.Item>
          </div>

          <Form.Item className="mb-0 mt-4">
            <Space>
              <Button type="primary" htmlType="submit">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
              <Button
                onClick={() => {
                  setPostModalVisible(false)
                  postForm.resetFields()
                  setEditingPost(null)
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Category Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Create Category'}
        open={categoryModalVisible}
        onCancel={() => {
          setCategoryModalVisible(false)
          categoryForm.resetFields()
          setEditingCategory(null)
        }}
        footer={null}
        width={500}
        destroyOnClose
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleCategorySubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input
              placeholder="Category name"
              onChange={(e) => {
                if (!editingCategory) {
                  categoryForm.setFieldValue('slug', generateSlug(e.target.value))
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="slug"
            label="Slug"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="category-slug" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Category description" />
          </Form.Item>

          <Form.Item name="sort_order" label="Sort Order">
            <Input type="number" placeholder="0" />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setCategoryModalVisible(false)
                  categoryForm.resetFields()
                  setEditingCategory(null)
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Blog
